import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

interface MonthContextType {
  currentMonth: Date;
  goToPreviousMonth: () => void;
  goToNextMonth: () => void;
  getMonthName: (date: Date) => string;
  getPreviousMonthName: () => string;
  getNextMonthName: () => string;
}

const MonthContext = createContext<MonthContextType | undefined>(undefined);

interface MonthProviderProps {
  children: ReactNode;
}

// The selected month lives in the URL (?manad=YYYY-MM) so a page reload or a
// shared link keeps the month. The current month is represented by NO param,
// keeping the default URL clean.
const parseMonthParam = (value: string | null): Date | null => {
  const match = value ? /^(\d{4})-(\d{2})$/.exec(value) : null;
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return new Date(Number(match[1]), month - 1, 1);
};

export const MonthProvider: React.FC<MonthProviderProps> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const monthParam = searchParams.get('manad');

  // Always anchor on the FIRST of the month - stepping months from e.g.
  // July 31 would otherwise overflow ("June 31" -> July 1) and make the
  // previous-month button a no-op at month ends.
  // Memoized on the param string: pages use currentMonth as an effect
  // dependency, so the Date reference must be stable across renders.
  const currentMonth = useMemo(() => {
    const parsed = parseMonthParam(monthParam);
    if (parsed) return parsed;
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }, [monthParam]);

  const setMonth = (date: Date) => {
    const now = new Date();
    const isCurrent = date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (isCurrent) {
        next.delete('manad');
      } else {
        next.set('manad', `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
      }
      return next;
    }, { replace: true });
  };

  const goToPreviousMonth = () => {
    setMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const getMonthName = (date: Date) => {
    const months = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'];
    return months[date.getMonth()];
  };

  const getPreviousMonthName = () => {
    return getMonthName(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const getNextMonthName = () => {
    return getMonthName(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const value: MonthContextType = {
    currentMonth,
    goToPreviousMonth,
    goToNextMonth,
    getMonthName,
    getPreviousMonthName,
    getNextMonthName,
  };

  return (
    <MonthContext.Provider value={value}>
      {children}
    </MonthContext.Provider>
  );
};

export const useMonth = (): MonthContextType => {
  const context = useContext(MonthContext);
  if (context === undefined) {
    throw new Error('useMonth must be used within a MonthProvider');
  }
  return context;
};
